import { includes } from 'lodash-es'
import * as Sentry from '@sentry/vue'
import { v4 as uuidV4 } from 'uuid'

import { isFeatureEnabled } from '@/store/modules/feature-toggles.module'
import participantInfoApi from '@/api/participant-info'
import Constant from '@/constant'
import storage from '@/services/storage'
import audienceApi from '@/api/audience'
import UnleashService from '@/services/unleash'

const getStoredAudienceIds = () => {
  return Object.keys(localStorage)
    .filter((key) => key.startsWith('aha-presentation-'))
    .map((key) => {
      try {
        const parsed = JSON.parse(localStorage.getItem(key))
        const audienceId = parsed.d
        if (!audienceId) {
          return null
        }
        return {
          audienceId,
        }
      } catch {
        return null
      }
    })
    .filter((value) => value !== null)
}

const mappingParticipantFields = (additionalFields, participantFields) => {
  const setFieldsMap = new Map()
  participantFields.forEach((field) => {
    setFieldsMap.set(field.id, field)
  })
  additionalFields = additionalFields.map((field) => {
    const fieldDetail = setFieldsMap.get(field.id) || {}
    return {
      ...fieldDetail,
      ...field,
    }
  })
  return additionalFields
}

const generateDeviceId = () => {
  // universalAudienceId is the public name, but internally we'll address it as device id.
  let deviceId = storage.getParticipantGlobalValue('universalAudienceId')
  if (!deviceId) {
    deviceId = `di_${uuidV4().replace(/-/g, '')}`
    storage.setParticipantGlobalValue('universalAudienceId', deviceId)
  }
  return deviceId
}

export const generateAudienceIdPerPresentationFromDeviceId = () => {
  const deviceId = generateDeviceId()
  return `${deviceId}-${uuidV4().replace(/-/g, '').substring(0, 16)}`
}

const _state = {
  customFieldsData: {},
  audienceAdmission: [],
}

const _getters = {
  canUseCollectParticipantInfo: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-collect-participant-info')
  },
  canBeAssignedCookie: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-participant-cookie-association')
  },
  additionalFields: (state, getters, rootState, rootGetters) => {
    if (getters.canUseCollectParticipantInfo) {
      const additionalFields = rootState.slide?.selectedParticipantFields || []
      return mappingParticipantFields(additionalFields, rootGetters['presentationAttributeModule/presentationParticipantFields'])
    }
    const fields = rootState.slide?.additionalFields
    return Array.isArray(fields) ? fields : []
  },
  shouldCollectAudienceInfo: (_, getters, rootState) => {
    const {
      disableFormJoinPresentation,
    } = rootState.presentation
    const audienceAdmission = getters.audienceAdmission || []
    const turnOnForm = rootState.presentation?.participantFields?.isAudienceAdmission ?? false

    if (disableFormJoinPresentation || !turnOnForm || !audienceAdmission?.length) return false
    return audienceAdmission.some((item) => {
      switch (item.type) {
        case 'name': return !rootState.audienceName
        case 'email': return !rootState.audienceEmail
        case 'team': return !rootState.audienceTeam
        case 'avatar': return !rootState.audienceEmoji
        default: return (!Constant.systemField.includes(item.type) && !storage.getValueByPresentation(rootState.presentation.id, `customField${item.type}`, null))
      }
    })
  },
  audienceAdmission: (state, getters, rootState, rootGetters) => {
    if (state.audienceAdmission.length > 0) {
      return state.audienceAdmission
    }

    if (getters.canUseCollectParticipantInfo && rootState.presentation?.selectedParticipantFields) {
      const audienceAdmission = rootState.presentation.selectedParticipantFields || []
      return mappingParticipantFields(audienceAdmission, rootGetters['presentationAttributeModule/presentationParticipantFields'])
    }
    return rootState.presentation?.audienceAdmission?.fields || []
  },
}

const actions = {
  assignCookieAction: async (_, participantId) => {
    try {
      await audienceApi.createOrUpdateAudienceV2({
        participantId,
      })
    } catch (error) {
      // 0 = CORS error, or it hit browser cache
      if (error?.status === 0) return
      Sentry.captureMessage(new Error('assignCookieAction Error'), {
        level: 'warning',
        extra: {
          participantId,
        },
      })
    }
  },
  updateParticipantInfoAction: async ({ getters }, payload) => {
    if (!getters.canUseCollectParticipantInfo) return
    const { presentationId, audienceId } = payload
    try {
      await participantInfoApi.updateParticipantInfo(payload)
    } catch (error) {
      Sentry.captureMessage(new Error('updateParticipantInfoAction Error'), {
        level: 'warning',
        extra: {
          presentationId,
          audienceId,
          error,
        },
      })
    }
  },
  // Intention: generate a device id and pushing past audienceIds' (stored in local storage) associations with it.
  // Audience devices should still have this data lying around so we'll try to collect it once for each device.
  maybePushDeviceAssociations: async ({ rootGetters }) => {
    await UnleashService.waitForInitialization()
    // Make sure flags initialized.
    if (!rootGetters['featureToggles/shouldPushDeviceAssociations']) {
      return
    }
    // Originally we wanted to consider the presence of universalAudienceId in the global storage as denoting whether
    // the backfill/push should be done. But using a separate flag value makes it more flexible and safe.
    if (storage.getParticipantGlobalValue('audienceIdAssociationSet')) {
      return
    }
    try {
      const deviceId = generateDeviceId()
      // Make sure we don't include new audienceId format which already has device id embedded into it.
      const associations = getStoredAudienceIds().filter(({ audienceId }) => !includes(audienceId, '-'))

      if (associations.length > 0) {
        await audienceApi.pushDeviceAssociations(deviceId, associations)
      }
      // Set this to true whether the device have any data to push or not. We will not do this check again.
      // If the participant clears the local data, this flag would be wiped but there won't be any data anyway,
      // so the API will not be called.
      storage.setParticipantGlobalValue('audienceIdAssociationSet', true)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  // same as maybePushDeviceAssociations but for cookie instead of local storage-based device id.
  maybePushCookieAssociations: async ({ rootGetters }) => {
    await UnleashService.waitForInitialization()
    // Make sure flags initialized.
    if (!rootGetters['featureToggles/shouldPushCookieAssociations']) {
      return
    }
    // Originally we wanted to consider the presence of universalAudienceId in the global storage as denoting whether
    // the backfill/push should be done. But using a separate flag value makes it more flexible and safe.
    if (storage.getParticipantGlobalValue('audienceIdAssociationWithCookieSet')) {
      return
    }
    try {
      const associations = getStoredAudienceIds()

      if (associations.length > 0) {
        await audienceApi.pushCookieAssociations(associations)
      }
      // Set this to true whether the device have any data to push or not. We will not do this check again.
      // If the participant clears the local data, this flag would be wiped but there won't be any data anyway,
      // so the API will not be called.
      storage.setParticipantGlobalValue('audienceIdAssociationWithCookieSet', true)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  updateCustomFieldsDataAction: ({ commit }, payload) => {
    commit('updateCustomFieldsDataMutation', payload)
  },
  updateAudienceAdmissionAction: ({ commit }, payload) => {
    commit('updateAudienceAdmissionMutation', payload)
  },
}

const mutations = {
  updateCustomFieldsDataMutation: (state, payload) => {
    state.customFieldsData = { ...state.customFieldsData, ...payload }
  },
  updateAudienceAdmissionMutation: (state, payload) => {
    state.audienceAdmission = payload
  },
}

const plugin =
  (store) => {
    store.subscribe(async (mutation) => {
      if (mutation.type !== 'setUserLoaded') {
        return
      }
      store.dispatch('participantModule/maybePushDeviceAssociations')
      store.dispatch('participantModule/maybePushCookieAssociations')
    })
  }

const assignCookiePlugin = (store) => {
  store.subscribeAction(async (action) => {
    const canBeAssignedCookie = (
      store.getters['participantModule/canBeAssignedCookie']
      && !store.getters['featureToggles/canUseCookieToLinkAudiences']
    )
    if (!canBeAssignedCookie) {
      return
    }

    if (action.type !== 'setAudienceIdAction') return
    store.dispatch('participantModule/assignCookieAction', action.payload)
  })
}

const plugins = [
  assignCookiePlugin,
]

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
  plugin,
  plugins,
}
